import React, { useState, useEffect } from 'react';
import { paymentsAPI, receiptsAPI, invoicesAPI, trustAccountsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { Plus, Receipt } from 'lucide-react';
import { toast } from 'react-toastify';

const METHODS = ['EFT', 'RTGS', 'Cash', 'Credit Card', 'Debit Card', 'Cheque', 'Mobile Money', 'Trust Transfer'];

const EMPTY = {
  invoice: '',
  amount_paid: '',
  payment_date: new Date().toISOString().slice(0, 10),
  payment_method: 'EFT',
  transaction_reference: '',
  trust_account_source: '',
  notes: '',
};

export default function Payments() {
  const [payments, setPayments]     = useState([]);
  const [invoices, setInvoices]     = useState([]);
  const [trustAccounts, setTrustAccounts] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [form, setForm]             = useState(EMPTY);
  const [saving, setSaving]         = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await paymentsAPI.list();
      setPayments(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Show invoices that still need payment
    invoicesAPI.list({ status: 'Sent' }).then(r => {
      const sent = r.data.results || r.data;
      invoicesAPI.list({ status: 'Partially Paid' }).then(r2 => {
        const partial = r2.data.results || r2.data;
        invoicesAPI.list({ status: 'Overdue' }).then(r3 => {
          setInvoices([...sent, ...(r2.data.results || r2.data), ...(r3.data.results || r3.data)]);
        });
      });
    });
    trustAccountsAPI.list().then(r => setTrustAccounts(r.data.results || r.data));
  }, []);

  const set = (name, val) => setForm(f => ({ ...f, [name]: val }));

  // Auto-fill amount from the selected invoice's balance_due
  const handleInvoiceChange = (id) => {
    set('invoice', id);
    const inv = invoices.find(i => String(i.id) === String(id));
    if (inv) set('amount_paid', parseFloat(inv.balance_due || inv.total_amount).toFixed(2));
  };

  // Auto-select trust account when client has one
  const handleMethodChange = (method) => {
    set('payment_method', method);
    if (method !== 'Trust Transfer') set('trust_account_source', '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.payment_method === 'Trust Transfer' && !form.trust_account_source) {
      toast.error('Please select a trust account for Trust Transfer payments.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.payment_method !== 'Trust Transfer') delete payload.trust_account_source;
      if (!payload.trust_account_source) delete payload.trust_account_source;

      const { data: payment } = await paymentsAPI.create(payload);
      await receiptsAPI.create({ payment: payment.id });
      toast.success('Payment recorded & receipt generated');
      if (form.payment_method === 'Trust Transfer') {
        toast.info('Trust account balance updated automatically.');
      }
      setModalOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      const detail = err.response?.data && typeof err.response.data === 'object'
        ? Object.entries(err.response.data).map(([k, v]) => `${k}: ${[].concat(v).join(', ')}`).join(' | ')
        : 'Failed to record payment';
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const { data: receipts } = await receiptsAPI.list({ payment: paymentId });
      const receipt = (receipts.results || receipts)[0];
      if (!receipt) return toast.error('No receipt found');
      const res = await receiptsAPI.pdf(receipt.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${receipt.receipt_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download receipt'); }
  };

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0);
  const isTrust   = form.payment_method === 'Trust Transfer';

  return (
    <div className="p-6">
      <PageHeader
        title="Payments & Receipts"
        subtitle="Record payments and generate receipts"
        actions={<button className="btn-primary" onClick={() => { setForm(EMPTY); setModalOpen(true); }}><Plus size={16} />Record Payment</button>}
      />

      <div className="card mb-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Payments Collected</p>
            <p className="text-3xl font-bold text-green-700 mt-1">
              US$ {totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-400 mt-1">{payments.length} payment records</p>
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>{['Date', 'Invoice', 'Client', 'Method', 'Reference', 'Amount', 'Receipt'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              : payments.length === 0 ? <tr><td colSpan={7} className="text-center py-12 text-gray-400">No payments yet.</td></tr>
              : payments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{p.payment_date}</td>
                  <td className="px-4 py-3 text-blue-700 font-medium">INV-{p.invoice_number}</td>
                  <td className="px-4 py-3 text-gray-700">{p.client_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${p.payment_method === 'Trust Transfer' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                      {p.payment_method}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{p.transaction_reference || '—'}</td>
                  <td className="px-4 py-3 font-bold text-green-700">US$ {parseFloat(p.amount_paid).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <button className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-200 rounded px-2 py-1"
                      onClick={() => downloadReceipt(p.id)}>
                      <Receipt size={11} /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Record Payment">
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">

          {/* Invoice */}
          <div className="col-span-2">
            <label className="label">Invoice *</label>
            <select className="input" required value={form.invoice} onChange={e => handleInvoiceChange(e.target.value)}>
              <option value="">Select invoice</option>
              {invoices.map(i => (
                <option key={i.id} value={i.id}>
                  INV-{i.invoice_number} — {i.client_name} — Balance: US$ {parseFloat(i.balance_due || i.total_amount).toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="label">Amount (US$) *</label>
            <input type="number" step="0.01" min="0.01" className="input" required
              value={form.amount_paid} onChange={e => set('amount_paid', e.target.value)} />
          </div>

          {/* Date */}
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required
              value={form.payment_date} onChange={e => set('payment_date', e.target.value)} />
          </div>

          {/* Method */}
          <div>
            <label className="label">Payment Method</label>
            <select className="input" value={form.payment_method} onChange={e => handleMethodChange(e.target.value)}>
              {METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>

          {/* Reference */}
          <div>
            <label className="label">Reference / Txn #</label>
            <input className="input" value={form.transaction_reference}
              onChange={e => set('transaction_reference', e.target.value)} />
          </div>

          {/* Trust Account — only shown when Trust Transfer is selected */}
          {isTrust && (
            <div className="col-span-2">
              <label className="label">Trust Account to Deduct From *</label>
              <select className="input" required value={form.trust_account_source}
                onChange={e => set('trust_account_source', e.target.value)}>
                <option value="">Select trust account</option>
                {trustAccounts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.client_name} — {a.bank_name} — Balance: US$ {parseFloat(a.current_balance).toFixed(2)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-purple-600 mt-1">
                A withdrawal will be automatically recorded and the trust balance updated.
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.notes}
              onChange={e => set('notes', e.target.value)} />
          </div>

          <div className="col-span-2 bg-blue-50 border border-blue-100 rounded p-3 text-xs text-blue-700">
            A PDF receipt is automatically generated when the payment is recorded.
          </div>

          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Processing...' : 'Record Payment'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
